import classnames from 'classnames';
import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import Modal from 'react-modal';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Document } from '../../../shared/icons';
import { LoadingDots } from '../../../shared/loading-dots/LoadingDots';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Text } from '../../../shared/text/Text';
import { Toggle } from '../../../shared/toggle';
import { OverflowTooltip } from '../../../shared/tooltip';
import { getAllClients } from '../../clients/store';
import { getAllDocumentNames } from '../../documents/store';
import { getAllAIAgreementTypeIds } from '../queries/store';
import styles from './FewShotDocuments.module.scss';
import { FewShotDocumentUploadInfo, getFilesToUpload, getFileUploadInformation, getIsUploading, getSelectedAgreementTypeId, setInitialDocuments, updateFewShotDocumentDetails, uploadFewShotDocumentsStarted } from './store';
import { MimeType } from '../../../constants/MimeType';

interface UploadModalProps {
    isOpen: boolean;
    closeModal: () => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({ isOpen, closeModal }) => {
    const dispatch = useAppDispatch();
    const files = useAppSelector(getFilesToUpload);
    const fileInformation = useAppSelector(getFileUploadInformation);
    const allDocumentNames = useAppSelector(getAllDocumentNames);
    const clients = useAppSelector(getAllClients);
    const aiAgreementTypeIds = useAppSelector(getAllAIAgreementTypeIds);
    const selectedAgreementTypeId = useAppSelector(getSelectedAgreementTypeId);
    const isUploading = useAppSelector(getIsUploading);

    const documentNameOptions: DropdownOption[] = useMemo(() => allDocumentNames.filter(({ agreementTypeId }) => !isNull(agreementTypeId) && aiAgreementTypeIds.includes(agreementTypeId) && (isNull(selectedAgreementTypeId) || agreementTypeId === selectedAgreementTypeId)).map(({ documentName, documentNameId }) => ({ value: documentNameId!.toString(), label: documentName })), [aiAgreementTypeIds, allDocumentNames, selectedAgreementTypeId]);
    const clientOptions: DropdownOption[] = useMemo(() => clients.map(({ clientId, clientName }) => ({ value: clientId!.toString(), label: clientName })), [clients]);

    const fileInformationComplete = useMemo(() => fileInformation.every(({ documentNameId, isSystem, clientId }) => !isNull(documentNameId) && (isSystem || !isNull(clientId))), [fileInformation]);

    const confirmDisabled = useMemo(() => !files || !fileInformationComplete, [files, fileInformationComplete]);

    const confirmDisabledTooltip = useMemo(() => {
        let disabledArray = !files ? ['You have not added any documents.'] : [];
        if (!fileInformationComplete) {
            disabledArray.push('Each document to be uploaded must have a document name and either specify the document as a system example or provide the relevant client.');
        }
        return disabledArray;
    }, [files, fileInformationComplete]);

    const onDrop = useCallback((acceptedFiles: File[]) => dispatch(setInitialDocuments(acceptedFiles)), [dispatch]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: true,
        maxFiles: 0,
        accept: {
            [MimeType.PDF]: [],
            [MimeType.DOCX]: [],
            [MimeType.MS_WORD]: [],
            [MimeType.TIFF]: []
        },
        onDrop
    });

    const getRelevantFileInformation = useCallback((index: number) => {
        const rawFileInformation = fileInformation[index];
        const documentNameValue = documentNameOptions.find(({ value }) => value === rawFileInformation.documentNameId?.toString()) || null;
        const clientValue = clientOptions.find(({ value }) => value === rawFileInformation.clientId?.toString()) || null;
        const scheduleStartPageValue = rawFileInformation.scheduleStartPage?.toString();
        const scheduleEndPageValue = rawFileInformation.scheduleEndPage?.toString();
        return { documentNameValue, clientValue, scheduleStartPageValue, scheduleEndPageValue, isSystem: rawFileInformation.isSystem };
    }, [documentNameOptions, clientOptions, fileInformation]);

    const testId = 'upload-few-shot-documents';

    const updateDropdownValue = useCallback((index: number, key: keyof FewShotDocumentUploadInfo, dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = parseInt((dropdownValue as DropdownOption).value);
        }
        dispatch(updateFewShotDocumentDetails(index, key, value));
        if (key === 'clientId' && fileInformation[index].isSystem) {
            dispatch(updateFewShotDocumentDetails(index, 'isSystem', false));
        }
    }, [dispatch, fileInformation]);

    const updateSchedulePage = useCallback((index: number, key: keyof FewShotDocumentUploadInfo, value: string) => {
        if (!isNaN(parseInt(value)) || value === '') {
            dispatch(updateFewShotDocumentDetails(index, key, value === '' ? null : parseInt(value)));
        }
    }, [dispatch]);

    const updateIsSystem = useCallback((index: number, value: boolean) => {
        dispatch(updateFewShotDocumentDetails(index, 'isSystem', value));
        if (value && !isNull(fileInformation[index].clientId)) {
            dispatch(updateFewShotDocumentDetails(index, 'clientId', null));
        }
    }, [dispatch, fileInformation]);

    const uploadDocuments = useCallback(() => dispatch(uploadFewShotDocumentsStarted()), [dispatch]);

    const documentsToUpload = useMemo(() => {
        if (!files || (files && !files.length)) {
            return <div>None</div>;
        }
        return <div className={styles.selectedDocumentsWrapper}>
            <div className={styles.tableHeader}>
                <div className={styles.headerCell} style={{ width: 'calc(25% - 11px)' }}><OverflowTooltip overlayText='Filename' className={styles.headerLabel} /></div>
                <div className={styles.headerCell} style={{ width: 'calc(20% - 11px)' }}><OverflowTooltip overlayText='Document Name' className={styles.headerLabel} /></div>
                <div className={styles.headerCell} style={{ width: 'calc(10% - 11px)' }}><OverflowTooltip overlayText='System?' className={styles.headerLabel} /></div>
                <div className={styles.headerCell} style={{ width: 'calc(15% - 11px)' }}><OverflowTooltip overlayText='Client' className={styles.headerLabel} /></div>
                <div className={styles.headerCell} style={{ width: 'calc(10% - 11px)' }}><OverflowTooltip overlayText='Schedule Start Page' className={styles.headerLabel} /></div>
                <div className={styles.headerCell} style={{ width: 'calc(10% - 11px)' }}><OverflowTooltip overlayText='Schedule End Page' className={styles.headerLabel} /></div>
                <div className={styles.sizeHeaderCell}><OverflowTooltip overlayText='Size' className={styles.headerLabel} /></div>
            </div>
            <Scrollable maxHeight='210px'>
                {files.map(({ name, size }, index) => {
                    const { documentNameValue, clientValue, isSystem, scheduleStartPageValue, scheduleEndPageValue } = getRelevantFileInformation(index);
                    return (
                        <div key={index} className={classnames(styles.tableRow, { [styles.lastTableRow]: index === files.length - 1 })}>
                            <div className={styles.cellWrapper} style={{ width: 'calc(25% - 12px)', justifyContent: 'flex-start' }}><OverflowTooltip overlayText={name} className={styles.cellLabel} /></div>
                            <div className={styles.cellWrapper} style={{ width: 'calc(20% - 11px)' }}>
                                <Dropdown
                                    value={documentNameValue}
                                    onChange={value => updateDropdownValue(index, 'documentNameId', value)}
                                    options={documentNameOptions}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className={styles.cellWrapper} style={{ width: 'calc(10% - 11px)' }}>
                                <Toggle
                                    checked={isSystem}
                                    onChange={checked => updateIsSystem(index, checked)}
                                />
                            </div>
                            <div className={styles.cellWrapper} style={{ width: 'calc(15% - 11px)' }}>
                                <Dropdown
                                    value={clientValue}
                                    onChange={value => updateDropdownValue(index, 'clientId', value)}
                                    options={clientOptions}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className={styles.cellWrapper} style={{ width: 'calc(10% - 11px)' }}>
                                <Text
                                    testId='few-shot-schedule-start-page'
                                    value={scheduleStartPageValue}
                                    onChange={e => updateSchedulePage(index, 'scheduleStartPage', e.target.value)}
                                    marginBottom='0px'
                                />
                            </div>
                            <div className={styles.cellWrapper} style={{ width: 'calc(10% - 11px)' }}>
                                <Text
                                    testId='few-shot-schedule-end-page'
                                    value={scheduleEndPageValue}
                                    onChange={e => updateSchedulePage(index, 'scheduleEndPage', e.target.value)}
                                    marginBottom='0px'
                                />
                            </div>
                            <div className={styles.sizeCellWrapper}><OverflowTooltip overlayText={`${(size / 1e6).toFixed(2)}MB`} className={styles.cellLabel} /></div>
                        </div>
                    );
                })}
            </Scrollable>
        </div>;
    }, [files, documentNameOptions, clientOptions, getRelevantFileInformation, updateDropdownValue, updateSchedulePage, updateIsSystem]);

    const documentTotal = files ? files.length : 0;
    const documentPlural = files && files.length > 1 ? 's' : '';

    const dropzoneText = useMemo(() => {
        if (isDragActive) {
            return 'Drop documents here';
        }
        if (files && files.length) {
            return `Document${documentPlural} successfully added, click here to change the document${documentPlural} to be uploaded`;
        }
        return 'Drag \'n\' drop documents here, or click to select files';
    }, [isDragActive, files, documentPlural]);

    if (isUploading) {
        return (
            <Modal
                ariaHideApp={false}
                isOpen
                className={styles.uploadingOverlay}
                style={{ overlay: { display: 'flex', zIndex: 10 } }}
            >
                <div className={styles.spinnerWrapper} data-testid={`${testId}-loading-wrapper`}>
                    <LoadingDots />
                    <div className={styles.spinnerLabel}>Uploading Document{documentPlural}</div>
                    <div className={styles.spinnerWarning}>Do not refresh or close browser during upload process</div>
                </div>
            </Modal>
        );
    }

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            confirmLabel='Upload'
            confirmDisabled={confirmDisabled}
            confirm={uploadDocuments}
            testId={testId}
            confirmDisabledTooltip={confirmDisabledTooltip}
            closeOnOverlayClick={false}
        >
            <div className={styles.uploadDocumentWrapper} data-testid={`${testId}-wrapper`}>
                <ModalHeader
                    label='Upload Few Shot Examples'
                    testId={testId}
                    icon={Document}
                />
                <div {...getRootProps({ className: styles.dropzone })}>
                    <input {...getInputProps()} />
                    <div className={styles.dropzonePlaceholder}>{dropzoneText}</div>
                </div>
                <div data-testid={`${testId}-selected-document-wrapper`} className={styles.selectedDocumentWrapper}>
                    <div className={styles.selectedDocumentHeader} data-testid={`${testId}-to-upload-label`}>{`Document${documentPlural} to be uploaded (${documentTotal})`}</div>
                    {documentsToUpload}
                </div>
            </div>
        </ConfirmationModal>
    );
};
